import axios from "axios";
import useAuth from "./useAuth";
import { isRealTenantId } from "../lib/tenantSlug";

/**
 * Single-flight refresh — audyt S10. Jeśli równolegle kilka requestów uzna,
 * że access token jest bliski wygaśnięcia (request interceptor +
 * równolegle response interceptor po 401), wywoływały refresh-token wiele
 * razy. Refresh token na backendzie potrafi się rotować/jednorazowo unieważnić,
 * więc drugi+ refresh dostawał błąd i wylogowywał użytkownika w trakcie pracy.
 *
 * Trzymamy aktualny in-flight Promise na poziomie modułu — kolejni wołający
 * czekają na ten sam wynik. Po zakończeniu (sukces lub błąd) czyścimy slot.
 */
let inFlight: Promise<string> | null = null;

const useRefreshToken = () => {
  const { auth, setAuth } = useAuth();
  const refresh = async (): Promise<string> => {
    if (inFlight) {
      return inFlight;
    }
    inFlight = (async () => {
      try {
        // Superadmin ma w JWT tenantId "public" — jego refresh idzie przez
        // /auth/refresh-token (wcześniej /tenant/public/... zawsze dawał 401).
        const tenantPrefix = isRealTenantId(auth?.tenantId) ? `/tenant/${auth.tenantId}` : "";
        const response = await axios.post(
          `${import.meta.env.VITE_API_URL}${tenantPrefix}/auth/refresh-token`,
          {
            refreshToken: auth.refreshToken,
          },
        );
        // Backend rotuje refresh token przy każdym odświeżeniu — zapisujemy
        // nowy. Zostawienie starego kończyło się wylogowaniem przy drugim
        // odświeżeniu (~30 min pracy).
        setAuth({
          ...auth,
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken ?? auth.refreshToken,
        });
        return response.data.accessToken as string;
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  };
  return refresh;
};

export default useRefreshToken;
